/**
 * DistrictMap - Full interactive map showing all district boundaries
 * Used on the dedicated Map page
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Tooltip, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import type { Layer, PathOptions, LeafletMouseEvent } from 'leaflet';
import type { Feature, Geometry } from 'geojson';
import { Spinner } from 'flowbite-react';
import polylabel from 'polylabel';
import { mapApi, leasApi, type DistrictPolygonsResponse, type DistrictPolygonProperties } from '../../api';
import type { SchoolPointsResponse } from '../../api/map';
import { AddressLocator } from './AddressLocator';
import 'leaflet/dist/leaflet.css';

// Hook to detect dark mode from Flowbite/Tailwind
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  return isDark;
};

// AIU Brand Colors - Light mode
const COLORS_LIGHT = {
  primary: '#004C97',      // Primary Blue - default fill
  light: '#009CDE',        // Light Blue - hover
  dark: '#003865',         // Dark Blue - borders
  teal: '#008C95',         // Teal
  red: '#A6192E',          // AIU Red (PMS 187) - highlighted
  green: '#00843D',        // Green
  locate: '#F59E0B',       // Amber - address-lookup matched district
  gray: '#C1C6C8',         // Gray
  labelText: '#003865',    // District label text
  labelShadow: 'white',    // District label shadow
  schoolLabelText: '#00843D',
  markerBg: 'white',
};

// AIU Brand Colors - Dark mode (lighter/brighter variants for visibility)
const COLORS_DARK = {
  primary: '#3B82F6',      // Brighter blue for dark backgrounds
  light: '#60A5FA',        // Light Blue - hover
  dark: '#93C5FD',         // Light border for contrast
  teal: '#14B8A6',         // Brighter teal
  red: '#EF4444',          // Brighter red for hover
  green: '#22C55E',        // Brighter green
  locate: '#FBBF24',       // Brighter amber - address-lookup matched district
  gray: '#9CA3AF',         // Gray
  labelText: '#E5E7EB',    // Light text for dark mode
  labelShadow: '#1F2937',  // Dark shadow for light text
  schoolLabelText: '#4ADE80',
  markerBg: '#374151',
};

// Pittsburgh area center
const DEFAULT_CENTER: [number, number] = [40.44, -79.99];
const DEFAULT_ZOOM = 10;

// AIU Headquarters (475 E Waterfront Dr, Homestead, PA)
const AIU_HQ_COORDS: [number, number] = [40.41350, -79.90878];

// Zoom thresholds
const LABEL_ZOOM_THRESHOLD = 11;
const SCHOOL_ZOOM_THRESHOLD = 12;
const SCHOOL_LABEL_ZOOM_THRESHOLD = 13;
const BASEMAP_LABELS_ZOOM_THRESHOLD = 13;

// Tile layer URLs
// CARTO watermarks unauthenticated raster requests with "API KEY REQUIRED";
// the key is free (5M tiles/month): https://carto.com/basemaps/apikey/
const CARTO_KEY = import.meta.env.VITE_CARTO_API_KEY;
const cartoTileUrl = (style: string) =>
  `https://basemaps.cartocdn.com/rastertiles/${style}/{z}/{x}/{y}{r}.png${CARTO_KEY ? `?key=${CARTO_KEY}` : ''}`;

const TILE_URLS = {
  light: {
    base: cartoTileUrl('light_nolabels'),
    labels: cartoTileUrl('light_only_labels'),
  },
  dark: {
    base: cartoTileUrl('dark_nolabels'),
    labels: cartoTileUrl('dark_only_labels'),
  },
};

interface DistrictMapProps {
  height?: string;
  onDistrictClick?: (leaId: number) => void;
}

// Compute optimal label point using pole of inaccessibility (polylabel)
function getLabelPoint(geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): [number, number] {
  let coords: number[][][];
  if (geometry.type === 'MultiPolygon') {
    let largest = geometry.coordinates[0];
    for (const poly of geometry.coordinates) {
      if (poly[0].length > largest[0].length) {
        largest = poly;
      }
    }
    coords = largest;
  } else {
    coords = geometry.coordinates;
  }
  const result = polylabel(coords, 0.001);
  return [result[1], result[0]];
}

// Component to track zoom level
const ZoomTracker: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });

  // Report initial zoom
  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  return null;
};

// District name labels rendered as divIcon markers at centroids
const DistrictLabels: React.FC<{
  polygons: DistrictPolygonsResponse;
  visible: boolean;
  colors: typeof COLORS_LIGHT;
}> = ({ polygons, visible, colors }) => {
  if (!visible) return null;

  return (
    <>
      {polygons.features.map((feature) => {
        const centroid = getLabelPoint(feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon);
        const name = feature.properties.shortName || feature.properties.name;

        const icon = L.divIcon({
          className: 'district-label',
          html: `<span style="
            display: block;
            max-width: 90px;
            font-size: 11px;
            font-weight: 600;
            line-height: 1.2;
            color: ${colors.labelText};
            text-shadow: 1px 1px 2px ${colors.labelShadow}, -1px -1px 2px ${colors.labelShadow}, 1px -1px 2px ${colors.labelShadow}, -1px 1px 2px ${colors.labelShadow};
            text-align: center;
            pointer-events: none;
          ">${name}</span>`,
          iconSize: [90, 0],
          iconAnchor: [45, 0],
        });

        return (
          <Marker
            key={`label-${feature.properties.educationOrganizationId}`}
            position={centroid}
            icon={icon}
            interactive={false}
          />
        );
      })}
    </>
  );
};

// School markers with tooltips and optional name labels
const SchoolMarkersWithTooltips: React.FC<{
  schools: SchoolPointsResponse | null;
  visible: boolean;
  showLabels: boolean;
  colors: typeof COLORS_LIGHT;
}> = ({ schools, visible, showLabels, colors }) => {
  const map = useMap();

  useEffect(() => {
    if (!visible || !schools) return;

    const layers: L.Layer[] = [];

    // Create SVG with current colors
    const schoolIconSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
  <path fill="${colors.green}" stroke="${colors.markerBg}" stroke-width="1.5"
    d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3z
       M18.82 9L12 12.72 5.18 9 12 5.28 18.82 9z"/>
</svg>`.trim();

    schools.features.forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const props = feature.properties;

      const iconHtml = `<div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        background: ${colors.markerBg};
        border-radius: 50%;
        box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      ">${schoolIconSvg}</div>`;

      const icon = L.divIcon({
        className: 'school-marker',
        html: iconHtml,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });

      const tooltipContent = `
        <div style="font-family: system-ui, sans-serif;">
          <strong style="font-size: 12px;">${props.name}</strong>
          ${props.schoolType ? `<br/><span style="color: #666; font-size: 11px;">${props.schoolType}</span>` : ''}
          ${props.districtName ? `<br/><span style="color: #666; font-size: 11px;">${props.districtName}</span>` : ''}
        </div>
      `;

      const marker = L.marker([lat, lng], { icon })
        .bindTooltip(tooltipContent, { direction: 'top', offset: [0, -12] })
        .addTo(map);

      layers.push(marker);

      // Permanent school name label (shown at deeper zoom)
      if (showLabels) {
        const labelIcon = L.divIcon({
          className: 'school-label',
          html: `<span style="
            display: block;
            max-width: 100px;
            font-size: 10px;
            font-weight: 500;
            line-height: 1.2;
            color: ${colors.schoolLabelText};
            text-shadow: 1px 1px 2px ${colors.labelShadow}, -1px -1px 2px ${colors.labelShadow}, 1px -1px 2px ${colors.labelShadow}, -1px 1px 2px ${colors.labelShadow};
            text-align: center;
            pointer-events: none;
          ">${props.name}</span>`,
          iconSize: [100, 0],
          iconAnchor: [50, -14],
        });

        const label = L.marker([lat, lng], { icon: labelIcon, interactive: false })
          .addTo(map);

        layers.push(label);
      }
    });

    return () => {
      layers.forEach((l) => l.remove());
    };
  }, [map, schools, visible, showLabels, colors]);

  return null;
};

export const DistrictMap: React.FC<DistrictMapProps> = ({
  height = '600px',
  onDistrictClick,
}) => {
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const [polygons, setPolygons] = useState<DistrictPolygonsResponse | null>(null);
  const [schools, setSchools] = useState<SchoolPointsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredOrgId, setHoveredOrgId] = useState<number | null>(null);
  const [locatedOrgId, setLocatedOrgId] = useState<number | null>(null);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);

  // Select colors based on dark mode
  const colors = isDarkMode ? COLORS_DARK : COLORS_LIGHT;
  const tileUrls = isDarkMode ? TILE_URLS.dark : TILE_URLS.light;

  // Fetch polygon and school data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [polygonData, schoolData] = await Promise.all([
          mapApi.getPolygons(),
          mapApi.getSchools().catch(() => null), // Don't fail if schools endpoint not ready
        ]);
        setPolygons(polygonData);
        setSchools(schoolData);
      } catch (err) {
        console.error('Failed to fetch map data:', err);
        setError('Failed to load map data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle click navigation
  const handleClick = useCallback((leaId: number) => {
    if (onDistrictClick) {
      onDistrictClick(leaId);
    } else {
      navigate(`/leas/${leaId}`);
    }
  }, [navigate, onDistrictClick]);

  // Style function for polygons
  const getStyle = useCallback((feature?: Feature<Geometry, DistrictPolygonProperties>): PathOptions => {
    const orgId = feature?.properties?.educationOrganizationId;
    const isHovered = orgId === hoveredOrgId;
    const isLocated = orgId != null && orgId === locatedOrgId;

    if (isLocated) {
      return {
        fillColor: colors.locate,
        fillOpacity: 0.6,
        color: colors.dark,
        weight: 4,
      };
    }

    return {
      fillColor: isHovered ? colors.red : colors.primary,
      fillOpacity: isHovered ? 0.5 : 0.4,
      color: colors.dark,
      weight: isHovered ? 3 : 1.5,
    };
  }, [hoveredOrgId, locatedOrgId, colors]);

  // Event handlers for each feature
  const onEachFeature = useCallback((
    feature: Feature<Geometry, DistrictPolygonProperties>,
    layer: Layer
  ) => {
    const props = feature.properties;

    // Mouse events
    layer.on({
      mouseover: (e: LeafletMouseEvent) => {
        setHoveredOrgId(props.educationOrganizationId);
        e.target.bringToFront();
      },
      mouseout: () => {
        setHoveredOrgId(null);
      },
      click: () => {
        handleClick(props.leaId);
      },
    });

    // Tooltip content (hover info card)
    const tooltipContent = `
      <div style="font-family: system-ui, sans-serif;">
        <strong style="font-size: 14px; color: ${COLORS_LIGHT.dark};">${props.name}</strong>
        ${props.leaType ? `<br/><span style="color: #666; font-size: 12px;">${props.leaType}</span>` : ''}
        ${props.enrollment ? `<br/><span style="font-size: 12px;">Enrollment: ${props.enrollment.toLocaleString()}</span>` : ''}
        ${props.superintendentName ? `<br/><span style="font-size: 12px;">Supt: ${props.superintendentName}</span>` : ''}
      </div>
    `;

    layer.bindTooltip(tooltipContent, {
      sticky: true,
      direction: 'top',
      offset: [0, -10],
    });
  }, [handleClick]);

  // Create a unique key for GeoJSON to force re-render when hover state or dark mode changes
  const geoJsonKey = useMemo(() => `geojson-${hoveredOrgId || 'none'}-${locatedOrgId || 'none'}-${isDarkMode ? 'dark' : 'light'}`, [hoveredOrgId, locatedOrgId, isDarkMode]);

  // Zoom-dependent visibility
  const showLabels = currentZoom >= LABEL_ZOOM_THRESHOLD;
  const showSchools = currentZoom >= SCHOOL_ZOOM_THRESHOLD;
  const showSchoolLabels = currentZoom >= SCHOOL_LABEL_ZOOM_THRESHOLD;
  const showBasemapLabels = currentZoom >= BASEMAP_LABELS_ZOOM_THRESHOLD;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg"
        style={{ height }}
      >
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg"
        style={{ height }}
      >
        <div className="text-center text-red-600 dark:text-red-400">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!polygons || polygons.features.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg"
        style={{ height }}
      >
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>No district boundary data available.</p>
          <p className="text-sm mt-1">Please run the boundary seed script.</p>
        </div>
      </div>
    );
  }

  // AIU HQ marker background adapts to dark mode
  const aiuMarkerBg = isDarkMode ? 'rgba(55, 65, 81, 0.95)' : 'rgba(255, 255, 255, 0.92)';

  return (
    <div className="relative" style={{ height, width: '100%' }}>
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      style={{ height: '100%', width: '100%' }}
      className="rounded-lg"
      key={isDarkMode ? 'dark' : 'light'} // Force re-mount when dark mode changes to swap tiles
      ref={(m) => { if (m && m !== mapInstance) setMapInstance(m); }}
      zoomControl={false} // Moved to top-right so the address search owns the top-left
    >
      <TileLayer
        url={tileUrls.base}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      />
      <ZoomControl position="topright" />
      <ZoomTracker onZoomChange={setCurrentZoom} />
      <GeoJSON
        key={geoJsonKey}
        data={polygons}
        style={getStyle}
        onEachFeature={onEachFeature}
      />
      {showBasemapLabels && (
        <TileLayer
          url={tileUrls.labels}
          zIndex={650}
        />
      )}
      <DistrictLabels polygons={polygons} visible={showLabels} colors={colors} />
      <SchoolMarkersWithTooltips schools={schools} visible={showSchools} showLabels={showSchoolLabels} colors={colors} />
      <Marker
        position={AIU_HQ_COORDS}
        icon={L.divIcon({
          className: 'aiu-hq-marker',
          html: `<div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 52px;
            height: 30px;
            background: ${aiuMarkerBg};
            border-radius: 5px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.2);
            padding: 3px;
            cursor: pointer;
          "><img src="/assets/images/aiu-logo.png" style="width: 46px; height: 20px;" alt="AIU" /></div>`,
          iconSize: [52, 30],
          iconAnchor: [26, 15],
          tooltipAnchor: [0, -17],
        })}
        eventHandlers={{
          click: async () => {
            const aiuName = import.meta.env.VITE_AIU_ORGANIZATION_NAME;
            const results = await leasApi.list({ search: aiuName, limit: 1 });
            if (results.length > 0) {
              navigate(`/leas/${results[0].leaId}`);
            }
          },
        }}
      >
        <Tooltip direction="top">
          <strong>Allegheny Intermediate Unit</strong><br />
          475 E Waterfront Dr, Homestead, PA
        </Tooltip>
      </Marker>
    </MapContainer>
    <AddressLocator
      map={mapInstance}
      polygons={polygons}
      schools={schools}
      colors={colors}
      onLocatedDistrict={setLocatedOrgId}
    />
    </div>
  );
};

export default DistrictMap;
